/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { createBrowserRouter } from "react-router-dom";
import ErrorPage from "./pages/Error";
import LoginPage from "./pages/Login";
import MiniVariantDrawer from "./pages/Layout/MiniVariantDrawer";
import { NODEROUTES, ROUTES, CONFIGUREROUTES, ADMINISTRATIONLIST } from "./globals/globals";
import Dashboard from "./pages/Dashboard/Dashboard";
import Settings from "./pages/Settings/Settings";
import DeviceDetails from "./pages/DeviceDetail/DeviceDetails";
import Nodes from "./pages/Nodes/Nodes";
import LogoutPage from "./pages/Logout";
import NodeDisplay from "./pages/Nodes/NodeDisplay/NodeDisplay";
import EventDisplay from "./pages/Nodes/EventDisplay/EventDisplay";
import AlarmDisplay from "./pages/Nodes/AlarmDisplay/AlarmDisplay";
import ConditionDisplay from "./pages/Nodes/ConditionDisplay/ConditionDisplay";
import RequireAuth from "./components/Auth/RequireAuth";
import Summary from "./pages/Configuration/Summary/Summary";
import IPInterfaces from "./pages/Configuration/IPInterfaceConfig/IPInterfaces";
import RadioNodes from "./pages/Configuration/RadioNode/RadioNodes";
import DasRadioNodes from "./pages/Configuration/DasRadioNodes/DasRadioNodes";
import NrCells from "./pages/Configuration/NrCell/NrCells";
import NgX2Connections from "./pages/Configuration/X2Connection/NgX2Connections";
import LteCells from "./pages/Configuration/LteCell/LteCells";
import Zones from "./pages/Configuration/Zones/Zones";
import Administrations from "./pages/Administrations/Administrations";
import Tasks from "./pages/Tasks/Tasks";
import AccessControls from "./pages/Administrations/AccessControls/AccessControls";
import AuditTrails from "./pages/Administrations/AuditTrails/AuditTrails";
import NorthboundInterfaces from "./pages/Administrations/NorthboundInterfaces/NorthboundInterfaces";
import Emails from "./pages/Administrations/Emails/Emails";
import FaultManagements from "./pages/Administrations/FaultManagements/FaultManagements";
import ServerSettings from "./pages/Administrations/ServerSettings/ServerSettings";
// import ServiceProvisioning from "./pages/Administrations/ServiceProvisioning/ServiceProvisioning";
// import PerformanceConfiguration from "./pages/Administrations/PerformanceConfiguration/PerformanceConfiguration";
// import GeographicalMaps from "./pages/Administrations/GeographicalMaps/GeographicalMaps";
// import UserDefinedDirectories from "./pages/Administrations/UserDefinedDirectories/UserDefinedDirectories";
// import DatabaseSettings from "./pages/Administrations/DatabaseSettings/DatabaseSettings";
import LteX2Connections from "./pages/Configuration/X2Connection/LteX2Connections";
import RfFeeds from "./pages/Configuration/RfFeed/RfFeeds";
import Rn620s from "./pages/Configuration/RN620/Rn620s";
import Inventory from "./pages/Configuration/Inventory/Inventory";
import DasInventory from "./pages/Configuration/DasInventory/DasInventory";
import DasTopology from "./pages/Configuration/DasTopology/DasTopology";
import HcmInfo from "./pages/Configuration/HcmInfo/HcmInfo";
import DcuInfo from "./pages/Configuration/DcuInfo/DcuInfo";
import ControllerInfo from "./pages/Configuration/ControllerInfo/ControllerInfo";
import Iframe from "./components/Shared/IFrame/Iframe";
import RequireAdmin from "./components/Auth/RequireAdmin";
import Commissioning from "./pages/Configuration/Commissioning/Commissioning";
import RfFeedsLatest from "./pages/Configuration/RfFeedLatest/RfFeedsLatest";
import FileManagements from "./pages/Administrations/FileManagements/FileManagements";
import DistributedUnit from "./pages/Configuration/DistributedUnits/Dus";
import DeviceMigration from "./pages/Administrations/Migration/DeviceMigration";
import Performance from "./pages/DeviceDetail/Performance/Performance";
import Files from "./pages/Configuration/Files/Files";
import DeviceLocationMap from "./pages/DeviceDetail/DeviceLocationMap/DeviceLocationMap";
import GeoRedundancy from "./pages/Administrations/GeoRedundancy/GeoRedundancy";
import ExternalPerformance from "./pages/ExternalPerformance/ExternalPerformance";
import ExternalPerformanceDisplay from "./pages/Nodes/ExternalPerformanceDisplay/ExternalPerformanceDisplay";
import ChangePassword from "./pages/Administrations/AccessControls/ChangePassword/ChangePassword";
import SnmpDetails from "./pages/Configuration/SnmpDetails/SnmpDetails";
import PerformanceSettings from "./pages/Administrations/PerformanceSettings/PerformanceSettings";
import LabelManagements from "./pages/Administrations/LabelManagements/LabelManagements";
import InventoryDisplay from "./pages/Nodes/InventoryDisplay/InventoryDisplay";

export const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN.Path,
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.PASSWORD.Path,
    index: true,
    element: <ChangePassword />,
  },
  {
    element: <RequireAuth />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: ROUTES.HOME.Path,
        element: <MiniVariantDrawer />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: ROUTES.DASHBOARD.Path,
            index: true,
            element: <Dashboard />,
          },
          {
            path: ROUTES.NODES.Path,
            element: <Nodes />,
            errorElement: <ErrorPage />,
            children: [
              {
                path: NODEROUTES.NODESPANEL.Path,
                index: true,
                element: <NodeDisplay />,
              },
              {
                path: NODEROUTES.EVENTSPANEL.Path,
                element: <EventDisplay />,
              },
              {
                path: NODEROUTES.ALARMSPANEL.Path,
                element: <AlarmDisplay />,
              },
              {
                path: NODEROUTES.CONDITIONSPANEL.Path,
                element: <ConditionDisplay />,
              },
              {
                path: NODEROUTES.PERFORMANCEPANEL.Path,
                element: <ExternalPerformanceDisplay />,
              },
              {
                path: NODEROUTES.INVENTORYPANEL.Path,
                element: <InventoryDisplay />,
              },
            ],
          },
          {
            path: ROUTES.DEVICEDETAILS.Path,
            element: <DeviceDetails />,
            children: [
              {
                index: true,
                path: CONFIGUREROUTES.NODESUMMARY.Path,
                element: <Summary />,
              },
              {
                path: CONFIGUREROUTES.INVENTORY.Path,
                element: <Inventory />,
              },
              {
                path: CONFIGUREROUTES.DASINVENTORY.Path,
                element: <DasInventory />,
              },
              {
                path: CONFIGUREROUTES.DASTOPOLOGY.Path,
                element: <DasTopology />,
              },
              {
                path: CONFIGUREROUTES.HCMINFO.Path,
                element: <HcmInfo />,
              },
              {
                path: CONFIGUREROUTES.DCUINFO.Path,
                element: <DcuInfo />,
              },
              {
                path: CONFIGUREROUTES.CONTROLLERINFO.Path,
                element: <ControllerInfo />,
              },
              {
                path: CONFIGUREROUTES.RN620.Path,
                element: <Rn620s />,
              },
              {
                path: CONFIGUREROUTES.RFFEED.Path,
                element: <RfFeeds />,
              },
              {
                path: CONFIGUREROUTES.RFFEEDLATEST.Path,
                element: <RfFeedsLatest />,
              },
              {
                path: CONFIGUREROUTES.IPINTERFACE.Path,
                element: <IPInterfaces />,
              },
              {
                path: CONFIGUREROUTES.RADIONODE.Path,
                element: <RadioNodes />,
              },
              {
                path: CONFIGUREROUTES.DASRADIONODES.Path,
                element: <DasRadioNodes />,
              },
              {
                path: CONFIGUREROUTES.NRCELL.Path,
                element: <NrCells />,
              },
              {
                path: CONFIGUREROUTES.LTECELL.Path,
                element: <LteCells />,
              },
              {
                path: CONFIGUREROUTES.ZONES.Path,
                element: <Zones />,
              },
              {
                path: CONFIGUREROUTES.NGX2CONNECTION.Path,
                element: <NgX2Connections />,
              },
              {
                path: CONFIGUREROUTES.LTEX2CONNECTION.Path,
                element: <LteX2Connections />,
              },
              {
                path: CONFIGUREROUTES.SSH.Path,
                element: <Iframe />,
              },
              {
                path: CONFIGUREROUTES.COMMISSIONING.Path,
                element: <Commissioning />,
              },
              {
                path: CONFIGUREROUTES.DU.Path,
                element: <DistributedUnit />,
              },
              {
                path: CONFIGUREROUTES.SUPERSET.Path,
                element: <Performance />,
              },
              {
                path: CONFIGUREROUTES.FILES.Path,
                element: <Files />,
              },
              {
                path: CONFIGUREROUTES.SNMPDETAILS.Path,
                element: <SnmpDetails />,
              },
              {
                path: CONFIGUREROUTES.LOCATION.Path,
                element: <DeviceLocationMap />,
              },
            ],
          },
          // {
          //   path: ROUTES.SERVICES.Path,
          //   element: <Services/>,
          //   errorElement: <ErrorPage />,
          // },
          {
            path: ROUTES.TASKS.Path,
            element: <Tasks />,
            errorElement: <ErrorPage />,
          },
          {
            path: ROUTES.EXTERNALPERFORMANCE.Path,
            element: <ExternalPerformance />,
            errorElement: <ErrorPage />,
          },
          {
            path: ROUTES.SETTINGS.Path,
            element: <Settings />,
            errorElement: <ErrorPage />,
          },
          {
            path: ROUTES.ADMINISTRATION.Path,
            element: (
              <RequireAdmin>
                <Administrations />
              </RequireAdmin>
            ),
            errorElement: <ErrorPage />,
            children: [
              {
                index: true,
                path: ADMINISTRATIONLIST.ACCESSCONTROL.Path,
                element: <AccessControls />,
              },
              {
                path: ADMINISTRATIONLIST.AUDITTRAIL.Path,
                element: <AuditTrails />,
              },
              {
                path: ADMINISTRATIONLIST.NORTHBOUNDINTERFACE.Path,
                element: <NorthboundInterfaces />,
              },
              {
                path: ADMINISTRATIONLIST.EMAIL.Path,
                element: <Emails />,
              },
              {
                path: ADMINISTRATIONLIST.FILEMANAGEMENT.Path,
                element: <FileManagements />,
              },
              {
                path: ADMINISTRATIONLIST.FAULTMANAGEMENT.Path,
                element: <FaultManagements />,
              },
              {
                path: ADMINISTRATIONLIST.SERVERSETTINGS.Path,
                element: <ServerSettings />,
              },
              {
                path: ADMINISTRATIONLIST.MIGRATION.Path,
                element: <DeviceMigration />,
              },
              {
                path: ADMINISTRATIONLIST.REDUNDANCY.Path,
                element: <GeoRedundancy />,
              },
              {
                path: ADMINISTRATIONLIST.PERFORMANCE.Path,
                element: <PerformanceSettings />,
              },
              {
                path: ADMINISTRATIONLIST.LABELMANAGEMENT.Path,
                element: <LabelManagements />,
              },
              // {
              //   path: ADMINISTRATIONLIST.TRISYNC.Path,
              //   element: <TriSync />,
              // },
              /*
                                {
                                  path: ADMINISTRATIONLIST.DATABASESETTINGS.Path,
                                  element: <DatabaseSettings />,
                                },
                                {
                                  path: ADMINISTRATIONLIST.USERDEFINEDDIRECTORIES.Path,
                                  element: <UserDefinedDirectories />,
                                },
                                {
                                  path: ADMINISTRATIONLIST.GEOGRAPHICALMAPS.Path,
                                  element: <GeographicalMaps />,
                                },
                                {
                                  path: ADMINISTRATIONLIST.PERFORMANCECONFIGURATION.Path,
                                  element: <PerformanceConfiguration />,
                                },
                                {
                                  path: ADMINISTRATIONLIST.SERVICEPROVISIONING.Path,
                                  element: <ServiceProvisioning />,
                                },
              */
            ],
          },
          {
            path: ROUTES.LOGOUT.Path,
            element: <LogoutPage />,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <ErrorPage />,
  },
]);
